import { useState } from "react"
import { useField, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormHelperText, IconButton, Stack, Typography } from "@mui/material"
import { faRepeat, faPen, faTrash } from "@fortawesome/pro-solid-svg-icons"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../buttons/StandardButton"

import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { RepeatOrderDialog, RepeatOrderFormValues } from "../meetingDetails/RepeatOrderDialog"
import { getFormikFieldError } from "../../form/helpers"
import { formatRecurrence } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    editButton: {
        color: theme.palette.tailwind.blue[500],
    },
    iconRepeat: {
        marginTop: ".25rem",
    },
    button: {
        paddingLeft: ".25rem",
    },
}))

type RecurrenceProps = Readonly<{
    title?: string
    description: string
    label?: string
    defaultButtonClass?: string
}>

export function Recurrence({ title = "", label, description, defaultButtonClass }: RecurrenceProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    const [{ value: deliveries }] = useField<DeliveryTimesField>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const [{ value: recurrenceValue }, meta, recurrenceHelpers] = useField<Partial<RepeatOrderFormValues> | undefined>(
        DynamicConfigKey.RECURRENCY,
    )
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [repeatOrderDialog, setRepeatOrderDialog] = useState<boolean>(false)

    const handleOpenDialog = () => {
        setRepeatOrderDialog(true)
    }

    const handleSubmitRecurrence = (values: Partial<RepeatOrderFormValues>) => {
        setRepeatOrderDialog(false)
        recurrenceHelpers.setValue(values)
    }

    const handleDelete = () => {
        recurrenceHelpers.setValue(undefined)
    }

    const { helperText } = getFormikFieldError(meta, submitCount)

    return (
        <>
            <Stack gap="0.25rem">
                <Stack gap="1rem" width="100%">
                    <Stack flexDirection="row" gap="1rem" alignItems="center" justifyContent="space-between">
                        {title && <Typography variant="subtitle1">{title}</Typography>}
                        {!recurrenceValue && (
                            <StandardButton
                                type="button"
                                color="blue"
                                variant="text"
                                size="small"
                                fullWidth={false}
                                onClick={handleOpenDialog}
                                startIcon={<FontAwesomeIcon icon={faRepeat} />}
                                updateDefaultClasses={cx(defaultButtonClass, classes.button)}
                            >
                                {label || description}
                            </StandardButton>
                        )}
                    </Stack>
                    {recurrenceValue && (
                        <Stack gap=".5rem">
                            <Stack flexDirection="row" gap=".5rem">
                                <FontAwesomeIcon icon={faRepeat} className={cx(classes.icon, classes.iconRepeat)} />
                                <Typography variant="body1">
                                    {formatRecurrence({ values: recurrenceValue, l10n })}
                                </Typography>
                            </Stack>
                            <Stack flexDirection="row" gap=".75rem" alignItems="center">
                                <IconButton className={classes.editButton} size="small" onClick={handleOpenDialog}>
                                    <FontAwesomeIcon icon={faPen} />
                                </IconButton>
                                <IconButton className={classes.icon} size="small" onClick={handleDelete}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                {helperText && <FormHelperText error>{helperText}</FormHelperText>}
            </Stack>

            {repeatOrderDialog && (
                <RepeatOrderDialog
                    repeatOrderDialog={repeatOrderDialog}
                    onClose={() => {
                        setRepeatOrderDialog(false)
                    }}
                    initialValues={recurrenceValue}
                    handleSubmitRecurrence={handleSubmitRecurrence}
                    deliveries={deliveries}
                />
            )}
        </>
    )
}
